import { Pencil, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { PageHeader } from "@/client/components/page-header";
import { Badge } from "@/client/components/ui/badge";
import { Button } from "@/client/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/client/components/ui/dialog";
import { Input } from "@/client/components/ui/input";
import { Label } from "@/client/components/ui/label";
import { Skeleton } from "@/client/components/ui/skeleton";
import {
  type AvailableDoc,
  type PlacementAdminView,
  placementsAdminApi,
} from "@/client/features/admin/placements-admin-api";
import { ApiError } from "@/client/lib/api";
import { displayDocTitle } from "@/shared/doc-title";
import type { DocSlot } from "@/shared/doc-slots";

const selectClass =
  "border-input bg-transparent focus-visible:border-ring focus-visible:ring-ring/50 h-9 w-full rounded-md border px-3 py-1 text-sm shadow-xs focus-visible:ring-[3px] focus-visible:outline-none";

function slotLabel(slots: DocSlot[], id: string): string {
  return slots.find((s) => s.id === id)?.label ?? id;
}

function AddPlacementForm({
  slots,
  docs,
  onCreated,
}: {
  slots: DocSlot[];
  docs: AvailableDoc[];
  onCreated: () => void;
}) {
  const [slot, setSlot] = useState(slots[0]?.id ?? "");
  const [outlineDocumentId, setOutlineDocumentId] = useState("");
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [position, setPosition] = useState("0");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!slot) {
      toast.error("Choose a slot.");
      return;
    }
    if (!outlineDocumentId) {
      toast.error("Choose a guide to place.");
      return;
    }
    setSubmitting(true);
    try {
      await placementsAdminApi.create({
        slot,
        outlineDocumentId,
        label: label.trim() || undefined,
        description: description.trim() || undefined,
        position: Number(position) || 0,
      });
      toast.success("Guide placed.");
      setOutlineDocumentId("");
      setLabel("");
      setDescription("");
      setPosition("0");
      onCreated();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to place guide.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Place a guide</CardTitle>
        <CardDescription>
          Map a published guide to a portal location. It appears only for tenants that have the
          guide published.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <label>
            <span className="mb-1 block text-sm font-medium">Slot</span>
            <select className={selectClass} value={slot} onChange={(e) => setSlot(e.target.value)}>
              {slots.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="mb-1 block text-sm font-medium">Guide</span>
            {docs.length === 0 ? (
              <p className="text-muted-foreground py-2 text-sm">
                No published guides yet. Publish Outline content to a tenant first.
              </p>
            ) : (
              <select
                className={selectClass}
                value={outlineDocumentId}
                onChange={(e) => setOutlineDocumentId(e.target.value)}
              >
                <option value="">Select a guide…</option>
                {docs.map((d) => (
                  <option key={d.outlineDocumentId} value={d.outlineDocumentId}>
                    {displayDocTitle(d.title)}
                  </option>
                ))}
              </select>
            )}
          </label>
          <label>
            <span className="mb-1 block text-sm font-medium">Label (optional)</span>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Overrides the guide's title"
            />
          </label>
          <label>
            <span className="mb-1 block text-sm font-medium">Position</span>
            <Input
              type="number"
              min={0}
              value={position}
              onChange={(e) => setPosition(e.target.value)}
            />
          </label>
          <label className="sm:col-span-2">
            <span className="mb-1 block text-sm font-medium">Description (optional)</span>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short blurb shown under guide cards"
            />
          </label>
        </div>
        <div>
          <Button onClick={submit} disabled={submitting || docs.length === 0}>
            {submitting ? "Placing…" : "Place guide"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function EditPlacementDialog({
  placement,
  open,
  onOpenChange,
  onSaved,
}: {
  placement: PlacementAdminView;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const [label, setLabel] = useState(placement.label ?? "");
  const [description, setDescription] = useState(placement.description ?? "");
  const [position, setPosition] = useState(String(placement.position));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setLabel(placement.label ?? "");
      setDescription(placement.description ?? "");
      setPosition(String(placement.position));
    }
  }, [open, placement]);

  const save = async () => {
    setSaving(true);
    try {
      await placementsAdminApi.update(placement.id, {
        label: label.trim() || null,
        description: description.trim() || null,
        position: Number(position) || 0,
      });
      toast.success("Saved.");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit placement</DialogTitle>
          <DialogDescription>
            {placement.docTitle ? displayDocTitle(placement.docTitle) : placement.outlineDocumentId}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="edit-label">Label</Label>
            <Input
              id="edit-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Overrides the guide's title"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="edit-description">Description</Label>
            <Input
              id="edit-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="edit-position">Position</Label>
            <Input
              id="edit-position"
              type="number"
              min={0}
              value={position}
              onChange={(e) => setPosition(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">Cancel</Button>
          </DialogClose>
          <Button onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function StaffDocsPlacementsPage() {
  const [slots, setSlots] = useState<DocSlot[]>([]);
  const [docs, setDocs] = useState<AvailableDoc[]>([]);
  const [optionsReady, setOptionsReady] = useState(false);
  const [optionsError, setOptionsError] = useState(false);
  const [placements, setPlacements] = useState<PlacementAdminView[] | null>(null);
  const [error, setError] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editing, setEditing] = useState<PlacementAdminView | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await placementsAdminApi.list();
      setPlacements(res.placements);
      setError(false);
    } catch (err) {
      setError(true);
      toast.error(err instanceof ApiError ? err.message : "Failed to load placements.");
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [slotsRes, docsRes] = await Promise.all([
          placementsAdminApi.listSlots(),
          placementsAdminApi.listAvailable(),
        ]);
        setSlots(slotsRes.slots);
        setDocs(docsRes.docs);
        setOptionsReady(true);
      } catch (err) {
        // An error here (e.g. a 403 for non-admin staff) must clear the loading
        // state, otherwise the form is stuck behind an endless skeleton.
        setOptionsError(true);
        toast.error(err instanceof ApiError ? err.message : "Failed to load admin data.");
      }
      await refresh();
    })();
  }, [refresh]);

  const onToggle = async (p: PlacementAdminView) => {
    setBusyId(p.id);
    try {
      await placementsAdminApi.update(p.id, { enabled: !p.enabled });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to update.");
      setBusyId(null);
      return;
    }
    // Reflect the change locally so the row isn't stale if the reload below
    // fails (that failure is reported separately by refresh()).
    setPlacements(
      (prev) => prev?.map((x) => (x.id === p.id ? { ...x, enabled: !p.enabled } : x)) ?? prev,
    );
    setBusyId(null);
    void refresh();
  };

  const onDelete = async (p: PlacementAdminView) => {
    if (!window.confirm(`Remove this guide from "${slotLabel(slots, p.slot)}"?`)) return;
    setBusyId(p.id);
    try {
      await placementsAdminApi.remove(p.id);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to remove.");
      setBusyId(null);
      return;
    }
    toast.success("Removed.");
    setPlacements((prev) => prev?.filter((x) => x.id !== p.id) ?? prev);
    setBusyId(null);
    void refresh();
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Guide placements"
        description="Surface published guides in context across the customer portal."
      />

      {optionsError ? (
        <Card>
          <CardContent className="text-muted-foreground py-6 text-center text-sm">
            Couldn't load placement options. Refresh to try again.
          </CardContent>
        </Card>
      ) : !optionsReady ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <AddPlacementForm slots={slots} docs={docs} onCreated={refresh} />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Placements</CardTitle>
          <CardDescription>Where each guide appears in the portal.</CardDescription>
        </CardHeader>
        <CardContent>
          {placements && placements.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground border-b text-left">
                    <th className="py-2 pr-4 font-medium">Slot</th>
                    <th className="py-2 pr-4 font-medium">Guide</th>
                    <th className="py-2 pr-4 font-medium">Position</th>
                    <th className="py-2 pr-4 font-medium">Status</th>
                    <th className="py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {placements.map((p) => (
                    <tr key={p.id} className="border-b last:border-0">
                      <td className="py-2 pr-4">{slotLabel(slots, p.slot)}</td>
                      <td className="py-2 pr-4">
                        <div className="font-medium">
                          {p.label ??
                            (p.docTitle ? displayDocTitle(p.docTitle) : p.outlineDocumentId)}
                        </div>
                        {p.label && p.docTitle ? (
                          <div className="text-muted-foreground text-xs">
                            {displayDocTitle(p.docTitle)}
                          </div>
                        ) : null}
                      </td>
                      <td className="text-muted-foreground py-2 pr-4">{p.position}</td>
                      <td className="py-2 pr-4">
                        {p.enabled ? (
                          <Badge variant="outline">Enabled</Badge>
                        ) : (
                          <Badge variant="secondary">Disabled</Badge>
                        )}
                      </td>
                      <td className="py-2">
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={busyId === p.id}
                            onClick={() => setEditing(p)}
                          >
                            <Pencil className="size-3.5" />
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={busyId === p.id}
                            onClick={() => onToggle(p)}
                          >
                            {p.enabled ? "Disable" : "Enable"}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={busyId === p.id}
                            onClick={() => onDelete(p)}
                          >
                            <Trash2 className="size-3.5" />
                            Remove
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : error ? (
            <p className="text-muted-foreground text-sm">
              Couldn't load placements. Refresh to try again.
            </p>
          ) : placements === null ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">No placements yet.</p>
          )}
        </CardContent>
      </Card>

      {editing ? (
        <EditPlacementDialog
          placement={editing}
          open={editing !== null}
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
          onSaved={refresh}
        />
      ) : null}
    </div>
  );
}
